import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductDurationRefs
 * @property {HTMLElement} durationContainer
 */

/**
 * A custom element that displays a product duration.
 * This component listens for variant update events and updates the duration display accordingly.
 * It handles duration updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductDurationRefs>}
 */
class ProductDuration extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateDuration);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateDuration);
  }

  /**
   * Updates the duration and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateDuration = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { durationContainer } = this.refs;
    // Find the new product-duration element in the updated HTML
    const newProductDuration = event.detail.data.html.querySelector(
      `product-duration[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductDuration) return;

    // Update duration container
    const newDuration = newProductDuration.querySelector('[ref="durationContainer"]');
    if (newDuration && durationContainer) {
      durationContainer.replaceWith(newDuration);
    }
  };
}

if (!customElements.get('product-duration')) {
  customElements.define('product-duration', ProductDuration);
}
